import {
  hasOrganization,
  selectOrganizationSelected,
} from "@app/organizations";
import { fetchPylonIdentityHash } from "@app/pylon";
import { useCache, useSelector } from "@app/react";
import { supportUrl } from "@app/routes";
import { selectCurrentUser } from "@app/users";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

declare global {
  interface Window {
    pylon?: {
      chat_settings: {
        app_id: string;
        email?: string;
        name?: string;
        avatar_url?: string;
        email_hash?: string;
        account_id?: string;
        account_external_id?: string;
      };
    };
    Pylon?: any;
  }
}

const PYLON_APP_ID =
  import.meta.env.VITE_PYLON_APP_ID || "f3e4a789-b693-4c63-a5ed-167c3c14e07a";
/**
 * PylonChatVisibility: Global hide/cleanup layer.
 * Mounted on all routes. Never fetches identity hash.
 * Hides Pylon widget when not on /support, user is unauthenticated, or org is not ready.
 */
const PylonChatVisibility = () => {
  const location = useLocation();
  const user = useSelector(selectCurrentUser);
  const org = useSelector(selectOrganizationSelected);
  const isOnSupportPage = location.pathname === supportUrl();
  const shouldShowWidget = isOnSupportPage && !!user.id && hasOrganization(org);

  useLayoutEffect(() => {
    if (!shouldShowWidget) {
      if (window.Pylon) {
        try {
          window.Pylon("hide");
        } catch (e) {
          // Ignore errors
        }
      }

      const style = document.createElement("style");
      style.id = "pylon-hide-style";
      style.textContent = `
        [id*="pylon"],
        [class*="pylon"],
        iframe[src*="pylon"],
        iframe[src*="usepylon"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
      `;
      document.head.appendChild(style);

      return () => {
        const existingStyle = document.getElementById("pylon-hide-style");
        if (existingStyle) {
          existingStyle.remove();
        }
      };
    }
    const existingStyle = document.getElementById("pylon-hide-style");
    if (existingStyle) {
      existingStyle.remove();
    }
  }, [shouldShowWidget]);

  useEffect(() => {
    if (!window.Pylon) return;

    if (!shouldShowWidget) {
      const persistentHideHandler = () => {
        if (
          (window.location.pathname !== supportUrl() ||
            !user.id ||
            !hasOrganization(org)) &&
          window.Pylon
        ) {
          try {
            window.Pylon("hide");
          } catch (e) {
            // Ignore errors
          }
        }
      };

      try {
        window.Pylon("onShow", persistentHideHandler);
      } catch (e) {
        // Ignore errors
      }

      return () => {
        try {
          window.Pylon("onShow", null);
        } catch (e) {
          // Ignore errors
        }
      };
    }
  }, [shouldShowWidget, user.id, org.id]);

  useEffect(() => {
    return () => {
      if (window.Pylon) {
        try {
          window.Pylon("hide");
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
    };
  }, []);

  return null;
};

/**
 * PylonSupportWidget: Fetches identity hash and initializes Pylon.
 * Only mounted when isOnSupportPage && user.id.
 * This gates the auth API call - useCache never runs on other routes.
 */
const PylonSupportWidget = () => {
  const user = useSelector(selectCurrentUser);
  const org = useSelector(selectOrganizationSelected);
  const [pylonReady, setPylonReady] = useState(false);
  const isMountedRef = useRef(true);

  const identityCache = useCache(
    fetchPylonIdentityHash({ userId: user.id, email: user.email }),
  );
  const emailHash = identityCache.data?.email_hash;
  const identityLoaded = !identityCache.isLoading;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (window.Pylon) {
        try {
          window.Pylon("hide");
        } catch (e) {}
      }
    };
  }, []);

  useEffect(() => {
    if (!pylonReady || !window.Pylon) return;

    const widgetReadyTime = Date.now();
    const autoOpenWindow = 1000;

    const showHandler = () => {
      const currentlyOnSupport = window.location.pathname === supportUrl();
      if (!currentlyOnSupport || !isMountedRef.current) {
        if (window.Pylon) {
          window.Pylon("hide");
        }
        return;
      }

      const timeSinceReady = Date.now() - widgetReadyTime;
      if (timeSinceReady < autoOpenWindow && window.Pylon) {
        window.Pylon("hide");
        try {
          window.Pylon("onShow", null);
        } catch (e) {}
      } else if (window.Pylon) {
        try {
          window.Pylon("onShow", null);
        } catch (e) {}
      }
    };

    window.Pylon("onShow", showHandler);

    const removeCallbackTimeout = setTimeout(() => {
      if (window.Pylon) {
        try {
          window.Pylon("onShow", null);
        } catch (e) {}
      }
    }, autoOpenWindow + 100);

    return () => {
      clearTimeout(removeCallbackTimeout);
      if (window.Pylon) {
        try {
          window.Pylon("hide");
          window.Pylon("onShow", null);
        } catch (e) {}
      }
    };
  }, [pylonReady]);

  useEffect(() => {
    if (!identityLoaded) return;

    try {
      window.pylon = {
        chat_settings: {
          app_id: PYLON_APP_ID,
          email: user.email,
          name: user.name,
          account_id: org.id,
          account_external_id: org.id,
          ...(emailHash && { email_hash: emailHash }),
        },
      };

      const existingScript = document.getElementById("pylon-widget");
      if (existingScript) {
        if (emailHash) {
          existingScript.remove();
          window.Pylon = undefined;
          setPylonReady(false);
          document
            .querySelectorAll(
              '[id*="pylon"], [class*="pylon"], iframe[src*="pylon"], iframe[src*="usepylon"]',
            )
            .forEach((el) => el.remove());
        } else {
          if (window.Pylon && !pylonReady) {
            setPylonReady(true);
          }
          return;
        }
      }

      const loadHandler = () => {
        if (document.getElementById("pylon-widget")) return;

        const script = document.createElement("script");
        script.setAttribute("type", "text/javascript");
        script.setAttribute("async", "true");
        script.setAttribute("id", "pylon-widget");
        script.setAttribute(
          "src",
          `https://widget.usepylon.com/widget/${PYLON_APP_ID}`,
        );
        script.onerror = () => {
          console.error("Failed to load Pylon chat widget");
        };

        script.onload = () => {
          if (isMountedRef.current) {
            setPylonReady(true);
          } else if (window.Pylon) {
            try {
              window.Pylon("hide");
            } catch (e) {}
          }
        };

        const firstScript = document.getElementsByTagName("script")[0];
        if (firstScript?.parentNode) {
          firstScript.parentNode.insertBefore(script, firstScript);
        }
      };

      if (document.readyState === "complete") {
        loadHandler();
      } else {
        window.addEventListener("load", loadHandler, false);
      }

      return () => {
        window.removeEventListener("load", loadHandler, false);
      };
    } catch (error) {
      console.error("Error initializing Pylon chat widget:", error);
    }
  }, [user.email, user.name, org.id, emailHash, identityLoaded]);

  return null;
};

// PylonChat: Top-level component mounted globally.
export const PylonChat = () => {
  const user = useSelector(selectCurrentUser);
  const org = useSelector(selectOrganizationSelected);
  const location = useLocation();
  const isOnSupportPage = location.pathname === supportUrl();
  const orgReady = hasOrganization(org);
  const shouldInitWidget = isOnSupportPage && !!user.id && orgReady;

  return (
    <>
      <PylonChatVisibility />
      {shouldInitWidget ? <PylonSupportWidget /> : null}
    </>
  );
};
